/**
 * Aplica formatação ao trecho selecionado do compositor de mensagem.
 * Entradas podem usar `action: '*texto*'` (placeholder "texto") ou `spec`.
 */

function normalizeSpec(entry) {
  if (entry?.spec) return entry.spec;
  if (entry?.action && typeof entry.action === "string" && entry.action.includes("texto")) {
    return { type: "wrap", template: entry.action };
  }
  return null;
}

function applyWrap(selected, template) {
  if (!template.includes("texto")) return selected;
  const [before, after] = template.split("texto");
  return `${before}${selected}${after}`;
}

function applyLinePrefix(selected, prefix) {
  return selected
    .split("\n")
    .map((line) => (line.trim() === "" ? line : `${prefix}${line.replace(/^(>\s*|-\s*|\d+\.\s*)/, "")}`))
    .join("\n");
}

function applyOrderedLines(selected) {
  return selected
    .split("\n")
    .map((line, i) => {
      if (line.trim() === "") return line;
      const cleaned = line.replace(/^(>\s*|-\s*|\d+\.\s*)/, "");
      return `${i + 1}. ${cleaned}`;
    })
    .join("\n");
}

function applyHtmlList(selected, ordered) {
  const lines = selected.split("\n").map((l) => l.trimEnd());
  const nonEmpty = lines.filter((l) => l.trim() !== "");
  const rows = nonEmpty.length ? nonEmpty : [selected.trim() || selected];
  const tag = ordered ? "ol" : "ul";
  const inner = rows.map((l) => `<li>${l}</li>`).join("\n");
  return `<${tag}>\n${inner}\n</${tag}>`;
}

function applyHtmlBlockquote(selected) {
  return `<blockquote>${selected.replace(/\n/g, "<br/>")}</blockquote>`;
}

/**
 * @param {string} message
 * @param {number} selStart
 * @param {number} selEnd
 * @param {object} entry item de formatOptions (action e/ou spec)
 * @returns {{ next: string, selStart: number, selEnd: number } | null}
 */
export function applyComposerTextFormat(message, selStart, selEnd, entry) {
  if (selStart == null || selEnd == null || selStart === selEnd) return null;
  const spec = normalizeSpec(entry);
  if (!spec || !spec.type) return null;

  const start = Math.min(selStart, selEnd);
  const end = Math.max(selStart, selEnd);
  const before = message.slice(0, start);
  const selected = message.slice(start, end);
  const after = message.slice(end);

  let middle = selected;

  switch (spec.type) {
    case "wrap": {
      if (!spec.template) return null;
      middle = applyWrap(selected, spec.template);
      break;
    }
    case "linePrefix": {
      if (spec.prefix == null) return null;
      middle = applyLinePrefix(selected, spec.prefix);
      break;
    }
    case "orderedLines": {
      middle = applyOrderedLines(selected);
      break;
    }
    case "htmlUl": {
      middle = applyHtmlList(selected, false);
      break;
    }
    case "htmlOl": {
      middle = applyHtmlList(selected, true);
      break;
    }
    case "htmlBlockquote": {
      middle = applyHtmlBlockquote(selected);
      break;
    }
    default:
      return null;
  }

  const next = before + middle + after;
  const newStart = start;
  const newEnd = start + middle.length;
  return { next, selStart: newStart, selEnd: newEnd };
}
